// eslint-disable max-classes-per-file
import type { MapAccessDeniedReason } from "./entitlements/types";

abstract class GameError extends Error {
  abstract readonly name: string;
  constructor(message: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

export class ParseMapError extends GameError {
  readonly name = "ParseMapError";
}

export class MapNotFoundError extends GameError {
  readonly name = "MapNotFoundError";
}

export class MapAccessDeniedError extends GameError {
  readonly name = "MapAccessDeniedError";
  constructor(
    readonly reason: MapAccessDeniedReason,
    readonly mapId: string,
  ) {
    super(`Map access denied (${reason}): ${mapId}`);
  }
}

export class EngineInvariantError extends GameError {
  readonly name = "EngineInvariantError";
}

export class DisplayNameTakenError extends GameError {
  readonly name = "DisplayNameTakenError";
}

export class InvalidDisplayNameError extends GameError {
  readonly name = "InvalidDisplayNameError";
}

export class InvalidMapRatingError extends GameError {
  readonly name = "InvalidMapRatingError";
  constructor(readonly rating: number) {
    super(`Invalid map rating: ${rating}`);
  }
}

export class PlayerNotFoundError extends GameError {
  readonly name = "PlayerNotFoundError";
  constructor(readonly playerId: string) {
    super(`Player ${playerId} not found`);
  }
}

export class PlayerCreateFailedError extends GameError {
  readonly name = "PlayerCreateFailedError";
  constructor(readonly playerId: string) {
    super(`Failed to create player ${playerId}`);
  }
}

export class DiscordAlreadyLinkedError extends GameError {
  readonly name = "DiscordAlreadyLinkedError";
  constructor() {
    super("This game account is already linked to Discord.");
  }
}

export class DiscordCodeInvalidError extends GameError {
  readonly name = "DiscordCodeInvalidError";
  constructor() {
    super("That verify code is invalid or has already been used.");
  }
}

export class DiscordCodeExpiredError extends GameError {
  readonly name = "DiscordCodeExpiredError";
  constructor() {
    super("That verify code has expired. Generate a new code in the game.");
  }
}

export class DiscordAccountTakenError extends GameError {
  readonly name = "DiscordAccountTakenError";
  constructor() {
    super("This Discord account is already linked to another game account.");
  }
}

export class DiscordNotLinkedError extends GameError {
  readonly name = "DiscordNotLinkedError";
  constructor() {
    super("This game account is not linked to Discord.");
  }
}

export class GameRunVersionConflictError extends GameError {
  readonly name = "GameRunVersionConflictError";
  constructor() {
    super("Game run was updated concurrently");
  }
}

export type MoveValidationCode =
  | "no_building"
  | "wrong_owner"
  | "building_occupied"
  | "unknown_unit_kind"
  | "not_buildable_here"
  | "insufficient_funds"
  | "no_airports"
  | "capacity_reached"
  | "already_moved"
  | "already_attacked"
  | "out_of_bounds"
  | "destination_occupied"
  | "out_of_range"
  | "cannot_damage_armor"
  | "indirect_after_move"
  | "not_transport"
  | "no_cargo"
  | "cannot_load"
  | "cannot_unload"
  | "preparing_unload"
  | "cannot_join"
  | "cannot_capture"
  | "already_owned"
  | "ally_target"
  | "path_empty"
  | "path_unreachable"
  | "path_wrong_endpoint"
  | "unit_not_found"
  | "game_not_in_progress";

export class MoveValidationError extends GameError {
  readonly name = "MoveValidationError";
  constructor(
    readonly code: MoveValidationCode,
    message: string,
  ) {
    super(message);
  }
}
